/* eslint-disable jsx-a11y/no-static-element-interactions */

/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import { ScrollArea } from "@base-ui/react/scroll-area"
import React, { useMemo } from "react"

import { stopPropagation } from "shared/lib/stopPropagation"
import { WithID } from "shared/types/WithID"

import { ItemsContext } from "./ItemsContext"
import { LoadingTable } from "./LoadingTable"
import { TableContext } from "./TableContext"
import { VirtualizedRowRenderer } from "./VirtualizedRowRenderer"
import { AnimationContextProvider } from "./animations/AnimationContextProvider"
import { Droppable } from "./dn-d/Droppable"
import { DragMoveContext, TableDnDContext } from "./dn-d/TableDnDContext"
import { useTableViewModel } from "./hooks/useTableViewModel"
import { TableProps } from "./types"

function TableComponent<I extends WithID>(props: TableProps<I>) {
  const {
    header,
    items,
    isLoading,
    loadingRows = 6,
    LoadingRowRenderer,
    multiSelect,
    noDataHint,
    containerProps
  } = props

  const {
    tableCanReceiveFocus,
    virtualizer,
    rowHeight,
    listContainerRef,
    tableContext,
    dndContext,
    dragMoveContext,
    flattenedTree,
    tableContainerRef,
    onKeyDown
  } = useTableViewModel(props)

  const hasNoRows = items.length === 0 && !isLoading

  const ariaProps = useMemo(() => {
    return {
      role: "treegrid",
      "aria-multiselectable": multiSelect,
      "aria-rowcount": flattenedTree.length
    }
  }, [multiSelect, flattenedTree.length])

  if (isLoading) {
    return (
      <LoadingTable
        loadingRows={loadingRows}
        LoadingRowRenderer={LoadingRowRenderer}
        containerProps={containerProps}
      />
    )
  }

  return (
    <>
      <div
        className="treegrid"
        ref={tableContainerRef}
        onKeyDown={onKeyDown}
        tabIndex={tableCanReceiveFocus ? 0 : -1}
        data-empty={hasNoRows ? "" : undefined}
        {...ariaProps}
        {...containerProps}
      >
        <div
          className="d-flex flex-grow-1"
          style={{ minHeight: 0 }}
          onBlur={stopPropagation}
        >
          {header}
          {hasNoRows ? null : (
            <TableContext.Provider value={tableContext}>
              <TableDnDContext.Provider value={dndContext}>
                <DragMoveContext.Provider value={dragMoveContext}>
                  <AnimationContextProvider>
                    <ItemsContext.Provider value={flattenedTree}>
                      <Droppable droppableID={null} level={0}>
                        <ScrollArea.Root className="scroll-area scroll-area--vertical virtual-list">
                          <ScrollArea.Viewport
                            ref={listContainerRef}
                            role="rowgroup"
                            className="scroll-area__viewport"
                            aria-label=""
                            tabIndex={-1}
                          >
                            <ScrollArea.Content
                              style={{
                                height: virtualizer.getTotalSize(),
                                position: "relative"
                              }}
                            >
                              {virtualizer.getVirtualItems().map(virtualRow => (
                                <VirtualizedRowRenderer
                                  key={virtualRow.index}
                                  index={virtualRow.index}
                                  style={{
                                    position: "absolute",
                                    top: virtualRow.start,
                                    left: 0,
                                    height: rowHeight,
                                    width: "100%"
                                  }}
                                />
                              ))}
                            </ScrollArea.Content>
                          </ScrollArea.Viewport>
                          <ScrollArea.Scrollbar
                            orientation="vertical"
                            className="scroll-area__scrollbar"
                          >
                            <ScrollArea.Thumb className="scroll-area__thumb" />
                          </ScrollArea.Scrollbar>
                        </ScrollArea.Root>
                      </Droppable>
                    </ItemsContext.Provider>
                  </AnimationContextProvider>
                </DragMoveContext.Provider>
              </TableDnDContext.Provider>
            </TableContext.Provider>
          )}
        </div>
      </div>
      {hasNoRows ? (
        <div className="treegrid-empty-note">{noDataHint}</div>
      ) : null}
    </>
  )
}
export const Table = React.memo(TableComponent) as <I extends WithID>(
  props: TableProps<I>
) => React.ReactElement | null
